/**
 * Where the site sends people, and how that changes between a laptop and production.
 *
 * The specification (§4.3) gives the production shape — accounts.clientma.com, app.clientma.com,
 * crm.clientma.com — but those resolve to nothing on a developer's machine, so every "Sign in"
 * and "Start trial" link on the local site was a dead end.
 *
 * Each destination is therefore read from the environment, with a default chosen by build mode:
 * local ports in development, the documented subdomains in production. Going live is a matter of
 * setting the variables below at build time; nothing here needs editing.
 *
 *   VITE_ACCOUNTS_URL   sign in            dev http://localhost:3001
 *   VITE_REGISTER_URL   create an account  dev falls back to the sign-in address
 *   VITE_APP_URL        app launcher       dev http://localhost:3001
 *   VITE_CRM_URL        CRM                dev http://localhost:3010
 *   VITE_API_DOCS_URL   API documentation  dev http://localhost:3001/settings/developer
 *
 * Vite inlines these at build time, so they are baked into the bundle — which is correct for
 * public addresses and is the reason none of them may ever hold a secret.
 */

const isDev = import.meta.env.DEV;

/** Trimmed, and empty-string treated as unset — an env file with `VITE_CRM_URL=` means "default". */
function configured(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed.replace(/\/+$/, '') : fallback;
}

const accounts = configured(
  import.meta.env.VITE_ACCOUNTS_URL,
  isDev ? 'http://localhost:3001' : 'https://accounts.clientma.com',
);

/**
 * Where the API is, for the two pages on this site that talk to it.
 *
 * Empty means same origin, which is right in development because Vite proxies `/api`. In
 * production the site and the API are different hostnames, and a relative path would be answered
 * by the site's own web server — which, because of the SPA rewrite rule, returns index.html with
 * a 200. The page then tries to read a signup response out of an HTML document and reports
 * something unintelligible instead of "that email is already registered".
 */
const apiOrigin = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '');

/** Absolute when an origin is configured, relative when it is not. */
export function apiUrl(path: string): string {
  return apiOrigin ? `${apiOrigin}${path}` : path;
}

export const endpoints = {
  signIn: accounts,

  /**
   * Registration.
   *
   * A path on this site rather than an absolute address: `/signup` is served here, and the call
   * to action buttons reach it through the router rather than through this value. Kept because
   * a deployment that hosts registration elsewhere needs somewhere to say so.
   *
   * The comment that used to sit here said the screen did not exist yet. It does —
   * `pages/SignUp.tsx` — and it is deliberately absent from the navigation, because it is
   * arrived at from a call to action rather than browsed to.
   */
  register: configured(import.meta.env.VITE_REGISTER_URL, '/signup'),

  launcher: configured(
    import.meta.env.VITE_APP_URL,
    isDev ? 'http://localhost:3001' : 'https://app.clientma.com',
  ),

  crm: configured(
    import.meta.env.VITE_CRM_URL,
    isDev ? 'http://localhost:3010' : 'https://crm.clientma.com',
  ),

  /**
   * API documentation.
   *
   * The workspace's own reference under Administration → Developer → REST API, not Swagger.
   * That page generates its object and field tables from the same metadata the API serves, so
   * it cannot describe a field that no longer exists — and it is written for the person on the
   * far side of an integration rather than for whoever wrote the controller.
   *
   * It sits behind sign-in, which the Resources page says plainly rather than implying a public
   * documentation site that does not exist.
   */
  api: configured(
    import.meta.env.VITE_API_DOCS_URL,
    isDev
      ? 'http://localhost:3001/settings/developer'
      : 'https://app.clientma.com/settings/developer',
  ),
} as const;
